import React from 'react';

// Catches any uncaught React render error in the subtree and shows a
// readable fallback. Without this, an exception during render blanks
// the screen because React 18 unmounts the whole tree on uncaught
// errors. Wrap the App body so a single bad component can't take
// down everything.

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        color: '#bfffbf',
        fontFamily: "'VT323', monospace",
        padding: 32,
      }}>
        <h1 style={{ color: '#ff3344', marginBottom: 16 }}>
          ┌── DASHBOARD CRASH ──┐
        </h1>
        <p style={{ color: '#5aaa5a', marginBottom: 16 }}>
          A component threw during render. The page would otherwise be black.
          Open the browser console for the full stack — the message below is
          the most-recent uncaught exception.
        </p>
        <pre style={{
          background: 'rgba(255,51,68,0.08)',
          border: '1px solid #ff3344',
          padding: 16,
          fontSize: 13,
          overflow: 'auto',
          maxWidth: '100%',
          marginBottom: 16,
        }}>
          {error.name}: {error.message}
          {error.stack ? '\n\n' + error.stack : ''}
          {info?.componentStack ? '\n\nComponent stack:' + info.componentStack : ''}
        </pre>
        <button
          onClick={this.reset}
          style={{
            background: '#0a0a0a',
            color: '#00ff41',
            border: '1px solid #00ff41',
            padding: '8px 16px',
            fontFamily: "'VT323', monospace",
            cursor: 'pointer',
            marginRight: 12,
          }}
        >
          [ TRY AGAIN ]
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#0a0a0a',
            color: '#5aaa5a',
            border: '1px solid #1e3a1e',
            padding: '8px 16px',
            fontFamily: "'VT323', monospace",
            cursor: 'pointer',
          }}
        >
          [ HARD RELOAD ]
        </button>
      </div>
    );
  }
}
